import React from 'react';

interface LoadingOverlayProps {
  /**
   * Whether to show the loading overlay
   */
  isLoading: boolean;
  /**
   * Optional message to display below the logo
   */
  message?: string;
  /**
   * Whether to use a fixed position (for full page) or absolute (for contained areas)
   * @default 'fixed'
   */
  position?: 'fixed' | 'absolute';
  /**
   * Z-index for the overlay
   * @default 200
   */
  zIndex?: number;
}

/**
 * Reusable loading overlay with co-taskr logo animation
 * Displays a bouncing co-taskr logo with pulsing glow effect
 */
export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  isLoading,
  message,
  position = 'fixed',
  zIndex = 200,
}) => {
  if (!isLoading) return null;

  return (
    <div
      className={`${position} inset-0 flex flex-col items-center justify-center bg-white/90 backdrop-blur-sm transition-opacity duration-300`}
      style={{ zIndex }}
    >
      <div className="relative">
        {/* Pulsing glow background */}
        <div className="absolute inset-0 rounded-xl bg-blue-700 opacity-20 blur-2xl animate-pulse" />
        
        {/* Co-taskr logo with bounce animation */}
        <div className="relative w-10 h-10 rounded-xl bg-blue-700 flex items-center justify-center shadow-2xl animate-bounce-slow">
          <svg
            className="w-5 h-5 text-white"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="14" width="7" height="7" rx="1" />
            <rect x="3" y="14" width="7" height="7" rx="1" />
          </svg>
        </div>
      </div>
      
      {/* Optional message */}
      {message && (
        <p className="mt-4 text-sm font-medium text-gray-600 animate-pulse">
          {message}
        </p>
      )}
    </div>
  );
};
